/**
 * @fileoverview Ingestion Stepper - Sidebar step navigation with completion indicators.
 *
 * Shows all 9 steps (0-8) with:
 * - Current step highlighted
 * - Completed steps with green checkmark
 * - Locked steps (before matter creation) grayed out
 * - Click to navigate (triggers form save first on audit steps)
 * - Ctrl/Cmd+click to open step in new tab
 */
import { AlertTriangle, Check } from 'lucide-react';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { CREATION_STEP_COUNT, UNIFIED_STEPS } from '../constants';
import { useUnifiedFlow } from '../context/UnifiedFlowContext';
import { useStepCompletion } from '../hooks/useStepCompletion';

/**
 * Map unified step IDs to completion status keys.
 */
const STEP_COMPLETION_MAP: Record<string, keyof ReturnType<typeof useStepCompletion>> = {
  basics: 'basics',
  case_info: 'case_info',
  client_info: 'client_info',
  liability: 'liability',
  treatment: 'treatment',
  coverage: 'coverage',
  documents: 'documents',
  case_summary: 'case_summary',
};

/**
 * Vertical sidebar stepper for case ingestion flow.
 * Shows all steps with runtime-computed completion status.
 * Click to navigate to any unlocked step.
 * Ctrl/Cmd+click opens step in new tab.
 */
export const IngestionStepper: React.FC = () => {
  const { state, requestNavigateToStep, assistState } = useUnifiedFlow();
  const { currentStepIndex, matterId, isSavingStep } = state;
  const location = useLocation();

  // Runtime-computed completion from actual data
  const completionStatus = useStepCompletion(matterId);

  // Build base path for step links (preserves /matters/:matterId/ingestion structure)
  const basePath = location.pathname;

  // Show skeleton while completion data is loading
  if (completionStatus.isLoading) {
    return (
      <nav className="py-4" aria-label="Progress">
        <ol className="space-y-0.5">
          {UNIFIED_STEPS.map((step) => (
            <li key={step.id} className="flex items-center gap-2 px-3 py-1.5">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-20" />
            </li>
          ))}
        </ol>
      </nav>
    );
  }

  return (
    <nav className="py-4" aria-label="Progress">
      <ol className="space-y-0.5">
        {UNIFIED_STEPS.map((step, index) => {
          const isLocked = index >= CREATION_STEP_COUNT && !matterId;
          const isCurrent = index === currentStepIndex;
          const isReviewStep = step.id === 'review';

          // Get completion status for this step (review step not tracked)
          const completionKey = STEP_COMPLETION_MAP[step.id];
          const isCompleted = completionKey ? completionStatus[completionKey] : false;

          // Check for unresolved high-severity gaps from assist
          const stepSummary = assistState.stepSummaries[step.id];
          const hasHighGaps = (stepSummary?.unresolvedHighCount ?? 0) > 0;

          // Build href for this step
          const stepHref = `${basePath}?step=${step.id}`;

          const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Allow Ctrl+click / Cmd+click to open in new tab (default anchor behavior)
            if (e.metaKey || e.ctrlKey) return;

            // Prevent default navigation for normal clicks
            e.preventDefault();

            if (isLocked || isSavingStep) return;
            requestNavigateToStep(index);
          };

          return (
            <li key={step.id}>
              <a
                href={stepHref}
                onClick={handleClick}
                aria-current={isCurrent ? 'step' : undefined}
                aria-disabled={isLocked || isSavingStep}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs transition-colors',
                  'hover:bg-zinc-100 dark:hover:bg-zinc-800',
                  isCurrent && 'bg-zinc-100 dark:bg-zinc-800 font-medium',
                  isLocked && 'opacity-70 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent pointer-events-none',
                  isSavingStep && 'cursor-wait'
                )}
              >
                <StepIndicator
                  index={index}
                  isCurrent={isCurrent}
                  isCompleted={isCompleted && !isReviewStep}
                  isLocked={isLocked}
                />
                <span className="truncate text-zinc-700 dark:text-zinc-300">
                  {step.shortLabel}
                </span>
                {hasHighGaps && !isCurrent && (
                  <AlertTriangle className="ml-auto h-3 w-3 flex-shrink-0 text-amber-500" />
                )}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Step indicator circle showing step number or completion check.
 */
const StepIndicator: React.FC<{
  index: number;
  isCurrent: boolean;
  isCompleted: boolean;
  isLocked: boolean;
}> = ({ index, isCurrent, isCompleted, isLocked }) => {
  // Completed - green check
  if (isCompleted) {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
        <Check className="h-3 w-3" />
      </span>
    );
  }

  // Current step - filled dark
  if (isCurrent) {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900 text-white text-[10px] font-medium dark:bg-white dark:text-zinc-900">
        {index + 1}
      </span>
    );
  }

  // Locked or default - outlined
  return (
    <span
      className={cn(
        'flex h-5 w-5 items-center justify-center rounded-full border text-[10px]',
        isLocked
          ? 'border-zinc-300 text-zinc-500 dark:border-zinc-600 dark:text-zinc-500'
          : 'border-zinc-300 text-zinc-500 dark:border-zinc-600 dark:text-zinc-400'
      )}
    >
      {index + 1}
    </span>
  );
};
